import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, useToast, errText, useCaptchaConfirm, TenantSelect } from '../ui';

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

// Reachable two ways:
//  - /admin/tenants/:clientCode/admin-user   (linked from Tenant Detail)
//  - /admin/tenants/tools/admin-user          (linked from the sidebar directly)
export default function AdminCredentials() {
  const { clientCode: routeClientCode } = useParams();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [clientCode, setClientCode] = useState(routeClientCode || '');
  const [tenantDbName, setTenantDbName] = useState('');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ newUsername: '', password: '', userType: 'Admin', expiryDate: '' });
  const [pwError, setPwError] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async (cc) => {
    setCurrent(null);
    try {
      const tenant = await tenantApi.getTenant(cc);
      const dbRes = await tenantApi.getDatabase(tenant.data.clientName);
      const db = dbRes.tenantDbName;
      setTenantDbName(db);
      const res = await tenantApi.getAdminUser(db);
      setCurrent(res);
      setForm((f) => ({ ...f, newUsername: res.userName || '', userType: res.userType || 'Admin' }));
    } catch (err) {
      toast.error(errText(err));
    }
  };

  useEffect(() => {
    if (clientCode) load(clientCode);
  }, [clientCode]); // eslint-disable-line

  const handleSave = async () => {
    if (form.password && !PASSWORD_RULE.test(form.password)) {
      setPwError('Min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special character.');
      return;
    }
    setPwError('');
    setSaving(true);
    try {
      const res = await tenantApi.updateAdminUser({ ...form, tenantDbName });
      toast.success('Admin credentials updated.');
      setCurrent(res);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const ok = await confirm('Reset admin credentials to the tenant default? Any custom username/password will be lost.');
    if (!ok) return;
    setResetting(true);
    try {
      await tenantApi.resetAdminUser(tenantDbName);
      toast.success('Admin credentials reset to default.');
      load(clientCode);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      {confirmNode}
      <h1 className="page-title">admin_credentials</h1>
      <p className="page-sub">
        {routeClientCode && (
          <>
            <Link to={`/admin/tenants/${routeClientCode}`} style={{ color: 'var(--text-dim)' }}>← {routeClientCode}</Link>
            {'  /  '}
          </>
        )}
        {tenantDbName && <>db: <b style={{ color: 'var(--green)' }}>{tenantDbName}</b></>}
      </p>

      {!routeClientCode && (
        <Panel title="// LOOKUP_TENANT">
          <div className="toolbar">
            <div className="toolbar__group">
              <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
            </div>
          </div>
        </Panel>
      )}

      {clientCode && (
        <>
          <Panel title="// CURRENT_ADMIN_USER">
            {!current ? <Loader label="fetching admin user" /> : (
              <div className="kv-grid">
                <dt>userName</dt><dd>{current.userName}</dd>
                <dt>userType</dt><dd>{current.userType}</dd>
                <dt>expiryDate</dt><dd>{current.expiryDate ? String(current.expiryDate).slice(0, 10) : '—'}</dd>
              </div>
            )}
          </Panel>

          {current && (
            <Panel title="// UPDATE_CREDENTIALS">
              <div className="form-grid">
                <div className="field">
                  <label>newUsername (3–50 chars, letters/digits/underscore)</label>
                  <input className="term-input" value={form.newUsername} onChange={(e) => setForm({ ...form, newUsername: e.target.value })} />
                </div>
                <div className="field">
                  <label>userType</label>
                  <select className="term-select" value={form.userType} onChange={(e) => setForm({ ...form, userType: e.target.value })}>
                    <option>Admin</option>
                    <option>Employee</option>
                  </select>
                </div>
                <div className="field field--full">
                  <label>password <span className="field-hint">(leave blank to keep current)</span></label>
                  <input type="password" className="term-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  {pwError && <div className="field-error">{pwError}</div>}
                </div>
                <div className="field">
                  <label>expiryDate</label>
                  <input type="date" className="term-input" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="term-btn term-btn--solid" disabled={saving} onClick={handleSave}>
                  {saving ? 'saving...' : 'save_changes'}
                </button>
                <button className="term-btn term-btn--danger" disabled={resetting} onClick={handleReset}>
                  {resetting ? 'resetting...' : 'reset_to_default'}
                </button>
              </div>
            </Panel>
          )}
        </>
      )}
    </>
  );
}
